import { lazy, Suspense } from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'

import { useFirebase } from '@scouts/auth'
import { GlobalSpinner } from '@taxfix/scouts-ui'

import {
    PATH_COMPANY_OFFICER_EMAIL_REFERRAL_AUTH,
    PATH_COMPANY_RETURN_QUESTIONS_UNREGISTERED_USER,
    PATH_COMPANY_RETURN_RESET,
    PATH_COMPANY_RETURN_START,
    PATH_COMPANY_SERVICE_ELIGIBILITY_START,
    PATH_COMPANY_SUBSCRIPTION_START,
    PATH_LOGOUT,
    PATH_MAGIC_LINK,
    PATH_MOBILE_NOTIFICATIONS,
    PATH_ROOT,
    PATH_TAX_RETURN,
    PATH_WELCOME,
} from '@/constants'
import { featureFlags } from '@/feature-flags'

import { BookkeepingConfigurationProvider } from '@/bookkeeping-configuration'

import { usePostHogFeatureFlags } from '@/hooks/use-posthog-feature-flags'

import { AuthenticationBoundary } from './auth/AuthenticationBoundary'
import { Logout } from './auth/Logout'
import { MagicLinkResolver } from './auth/MagicLinkResolver'
import { CompanyOfficerAuth } from './company-officer/CompanyOfficerAuth'
import { MobileNotifications } from './mobile-notifications/MobileNotifications'

const CompanyReturnQuestionnaireContainer = lazy(() => import('./company-return/CompanyReturnQuestionnaireContainer'))
const WelcomeContainer = lazy(() => import('./welcome/WelcomeContainer'))
const CompanyReturnStart = lazy(() => import('./company-return/CompanyReturnStart'))
const CompanyServiceEligibilityContainer = lazy(
    () => import('./company-service-eligibility/CompanyServiceEligibilityContainer')
)
const CompanySubscriptionStart = lazy(() => import('./company-subscription/start/CompanySubscriptionStartContainer'))
const TaxReturnContainer = lazy(() => import('./tax-return/TaxReturnContainer'))
const Main = lazy(() => import('./Main'))

const CompanyReturnQuestionnaireContainerSuspense = () => (
    <Suspense fallback={<GlobalSpinner />}>
        <CompanyReturnQuestionnaireContainer />
    </Suspense>
)
const WelcomeContainerSuspense = () => (
    <Suspense fallback={<GlobalSpinner />}>
        <WelcomeContainer />
    </Suspense>
)
const CompanyReturnStartSuspense = () => (
    <Suspense fallback={<GlobalSpinner />}>
        <CompanyReturnStart />
    </Suspense>
)
const CompanyServiceEligibilityContainerSuspense = () => (
    <Suspense fallback={<GlobalSpinner />}>
        <CompanyServiceEligibilityContainer />
    </Suspense>
)
const CompanySubscriptionStartSuspense = () => (
    <Suspense fallback={<GlobalSpinner />}>
        <CompanySubscriptionStart />
    </Suspense>
)
const TaxReturnContainerSuspense = () => (
    <Suspense fallback={<GlobalSpinner />}>
        <TaxReturnContainer />
    </Suspense>
)

export const AppRoutes = () => {
    const { user } = useFirebase()
    const posthogFeatureFlags = usePostHogFeatureFlags()

    return (
        <Switch>
            <Route path={PATH_LOGOUT} component={Logout} />
            <Route exact path={PATH_MAGIC_LINK} component={MagicLinkResolver} />
            <Route path={PATH_MOBILE_NOTIFICATIONS} component={MobileNotifications} />
            <Route path={PATH_WELCOME} component={WelcomeContainerSuspense} />
            {featureFlags.featureCompanyQuestionnaire && !user && (
                <Route path={PATH_COMPANY_RETURN_START}>
                    <Switch>
                        <Route path={PATH_COMPANY_RETURN_RESET}>
                            <Redirect to={PATH_COMPANY_RETURN_START} />
                        </Route>
                        <Route
                            path={PATH_COMPANY_RETURN_QUESTIONS_UNREGISTERED_USER}
                            component={CompanyReturnQuestionnaireContainerSuspense}
                        />
                        <Route path={PATH_COMPANY_RETURN_START} component={CompanyReturnStartSuspense} />
                    </Switch>
                </Route>
            )}
            {featureFlags.featureCompanySubscriptions && !user && (
                <Route
                    path={PATH_COMPANY_SERVICE_ELIGIBILITY_START}
                    component={CompanyServiceEligibilityContainerSuspense}
                />
            )}
            {featureFlags.featureCompanySubscriptions && !user && (
                <Route path={PATH_COMPANY_SUBSCRIPTION_START} component={CompanySubscriptionStartSuspense} />
            )}
            {posthogFeatureFlags.featureSelfAssessmentOnboardingRevamp && !user && (
                <Route path={PATH_TAX_RETURN.BASE} component={TaxReturnContainerSuspense} />
            )}
            <Route path={PATH_COMPANY_OFFICER_EMAIL_REFERRAL_AUTH} component={CompanyOfficerAuth} />
            <Route path={PATH_ROOT}>
                <AuthenticationBoundary>
                    <BookkeepingConfigurationProvider>
                        <Suspense fallback={<GlobalSpinner />}>
                            <Main />
                        </Suspense>
                    </BookkeepingConfigurationProvider>
                </AuthenticationBoundary>
            </Route>
        </Switch>
    )
}
